import React from "react";
import { getAllExploreData } from "../services/Explore";
import { ListView } from "../CommonComponents/ListView/ListView";
import { FilterPageLayout } from "../CommonComponents/FilterPageLayout/FilterPageLayout";
import { useFilterPage } from "../hooks/useFilterPage";
import { explore } from "../constants/constants";
import { getImageURL } from "../util/getImage";
import {
  Image,
  Box,
  Text,
  UnorderedList,
  ListItem,
  useToast,
} from "@chakra-ui/react";
import { handleExport as handleExportUtil } from "../util/helper";
import { useNavigate } from "react-router-dom";

const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`;

// The backend's natural-sort path once returned raw Neo4j Integers ({low, high})
// for the order slots; rendering one as a child crashed the page. Accept both.
const toOrder = (value) =>
  value != null && typeof value === "object" && "low" in value
    ? value.low
    : (value ?? null);

const citationFactor = (data) =>
  data?.map((item) => ({
    title: item?.title,
    alias: item?.alias,
    direct_url_of_cover_image: item?.imgPath,
    research_categories: Array.isArray(item?.research_categories)
      ? item.research_categories.map((cat) => ({
          name: cat.name,
          value: cat.value,
        }))
      : [],
    show_at: item?.show_at || "",
    subject: item?.subject,
    masterlist_type: item?.masterlist_type || "",
    masterlist_alias: item?.route_type,
    route_type: item?.route_type,
    navigationLink: item?.navigationLink,
    home_collage_order: toOrder(item?.home_collage_order),
    explore_carousel_order: toOrder(item?.explore_carousel_order),
    explore_search_hint_order: toOrder(item?.explore_search_hint_order),
  }));

const renderOrder = (value) =>
  value != null ? (
    <Text fontSize="sm">{value}</Text>
  ) : (
    <Text color="gray.500">-</Text>
  );

const columns = [
  { label: "Name of Masterlist", accessor: "title" },
  {
    label: "Image",
    accessor: "direct_url_of_cover_image",
    render: (value) => (
      <Image
        src={getImageURL(value, "200", "100")}
        width="200px"
        height="100px"
        objectFit="contain"
        fallbackSrc={placeholderImg}
        alt="Cover image"
      />
    ),
  },
  {
    label: "Research Categories",
    accessor: "research_categories",
    render: (value) => (
      <Box>
        {Array.isArray(value) && value.length > 0 ? (
          <UnorderedList spacing={1} pl={4}>
            {value.map((cat, i) => (
              <ListItem key={i} fontSize="sm" pb={1}>
                {cat.name}
              </ListItem>
            ))}
          </UnorderedList>
        ) : (
          <Text color="gray.500">-</Text>
        )}
      </Box>
    ),
  },
  { label: "Masterlist Type", accessor: "subject" },
  {
    label: "Masterlist Sub-Type",
    accessor: "masterlist_type",
    render: (value) =>
      value ? (
        <Text fontSize="sm">{value}</Text>
      ) : (
        <Text color="gray.500">-</Text>
      ),
  },
  { label: "Route Type", accessor: "route_type" },
  {
    label: "Home Collage Order",
    accessor: "home_collage_order",
    render: renderOrder,
  },
  {
    label: "Explore Carousel Order",
    accessor: "explore_carousel_order",
    render: renderOrder,
  },
  {
    label: "Search Hint Order",
    accessor: "explore_search_hint_order",
    render: renderOrder,
  },
];

const ExploreEdit = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const filterState = useFilterPage({
    limit: 20,
    fetchFn: getAllExploreData,
    normalizeResult: (raw) => ({
      records: citationFactor(raw?.data || []),
      total: raw?.total,
      facetsHeaders: raw?.facetsHeaders,
      facets: raw?.facets,
      pageNumber: raw?.page_number,
    }),
  });

  const {
    records,
    currentPage,
    totalPages,
    handlePageChange,
    loading,
    filterOptions,
    setFilterOptions,
    setApply,
  } = filterState;

  const sortOrder =
    filterOptions.sortBy_v === "sort_name_desc" ? "desc" : "asc";
  const sortRecords = () => {
    const next = sortOrder === "asc" ? "sort_name_desc" : "sort_name";
    setFilterOptions((prev) => ({ ...prev, sortBy_v: next }));
    setApply(true);
  };

  const handleEdit = (record) => navigate(record.navigationLink);
  const handleExport = () =>
    handleExportUtil("explore", toast, { search: filterState.searchText });

  return (
    <FilterPageLayout
      hook={filterState}
      toolbar={{
        title: "Explore",
        type: "explore",
        exportHandler: handleExport,
        sortRecords,
        sortOrder,
        pagination: false,
      }}
      filterBox={{ explore }}
    >
      <ListView
        loading={loading}
        records={records}
        columns={columns}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        handleEdit={handleEdit}
        selectable={false}
        pagination={true}
      />
    </FilterPageLayout>
  );
};

export default ExploreEdit;
